import React from 'react';

const Privacy = () => {
  return (
    <section id="privacy" className="privacy">
      <div className="privacy-container">
        <div className="privacy-content">
          <h2 className="section-title">Privacy Policy</h2>
          <p className="privacy-updated">Last Updated: February 21, 2026</p>
          
          <div className="privacy-section">
            <h3 className="privacy-subtitle">1. Introduction</h3>
            <p className="privacy-text">
              At Alium Software, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or use our applications and services.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">2. Information We Collect</h3>
            <h4 className="privacy-subheading">Personal Information</h4>
            <p className="privacy-text">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="privacy-list">
              <li>Contact us through our website or email</li>
              <li>Request information about our services</li>
              <li>Use our applications or consultancy services</li>
              <li>Subscribe to our communications</li>
            </ul>
            <p className="privacy-text">
              This information may include: name, email address, phone number, company name, job title, 
              and any other information you choose to provide.
            </p>

            <h4 className="privacy-subheading">Automatically Collected Information</h4>
            <p className="privacy-text">
              When you visit our website or use our applications, we may automatically collect certain information, including:
            </p>
            <ul className="privacy-list">
              <li>IP address and location data</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring website</li>
              <li>Pages viewed and time spent</li>
              <li>Device identifiers</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">3. How We Use Your Information</h3>
            <p className="privacy-text">We use collected information for the following purposes:</p>
            <ul className="privacy-list">
              <li>To provide and maintain our services</li>
              <li>To communicate with you about our services</li>
              <li>To respond to your inquiries and support requests</li>
              <li>To improve our website and applications</li>
              <li>To send you updates and marketing communications (with your consent)</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and prevent fraud</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">4. Information Sharing and Disclosure</h3>
            <p className="privacy-text">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, 
              except in the following circumstances:
            </p>
            <ul className="privacy-list">
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website and applications</li>
              <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
              <li><strong>Consent:</strong> We may share information with your explicit consent</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">5. Data Security</h3>
            <p className="privacy-text">
              We implement appropriate technical and organizational security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="privacy-list">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Employee training on data protection</li>
              <li>Secure cloud infrastructure with Azure</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">6. Data Retention</h3>
            <p className="privacy-text">
              We retain personal information only for as long as necessary to fulfill the purposes for which it was collected, 
              comply with legal obligations, resolve disputes, and enforce our agreements. When information is no longer needed, 
              we securely delete or anonymize it.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">7. Your Rights</h3>
            <p className="privacy-text">
              Depending on your jurisdiction, you may have the following rights regarding your personal information:
            </p>
            <ul className="privacy-list">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw previously given consent</li>
            </ul>
            <p className="privacy-text">
              To exercise these rights, please contact us using the information provided below.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">8. Cookies and Tracking Technologies</h3>
            <p className="privacy-text">
              Our website may use cookies and similar tracking technologies to enhance user experience and analyze website traffic. 
              You can control cookie preferences through your browser settings. Note that disabling cookies may affect website functionality.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">9. Third-Party Links</h3>
            <p className="privacy-text">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. 
              We encourage you to review the privacy policies of any third-party websites you visit.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">10. Children's Privacy</h3>
            <p className="privacy-text">
              Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children. 
              If we become aware that we have collected information from a child under 13, we will take steps to delete such information promptly.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">11. International Data Transfers</h3>
            <p className="privacy-text">
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards 
              are in place to protect your information in accordance with applicable data protection laws.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">12. Changes to This Privacy Policy</h3>
            <p className="privacy-text">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy 
              on this page and updating the "Last Updated" date. Continued use of our services after changes constitutes acceptance of the new policy.
            </p>
          </div>

          <div className="privacy-section">
            <h3 className="privacy-subtitle">13. Contact Information</h3>
            <p className="privacy-text">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="privacy-contact">
              <p><strong>Alium Software</strong></p>
              <p>Email: privacy@aliumsoftware.com</p>
              <p>General Contact: hello@aliumsoftware.com</p>
              <p>Phone: +1 (555) 123-4567</p>
            </div>
          </div>

          <div className="privacy-footer">
            <p className="privacy-notice">
              This privacy policy applies to all applications, websites, and services provided by Alium Software. 
              Like our company name, we may miss a letter, but we never miss a detail when it comes to protecting your privacy.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Privacy;